import Button from "@components/Button"
import HelpCenterLayout from "@components/helpCenter/HelpCenterLayout"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { useTranslation } from "react-i18next"

/**
 * The cancel and refund requests: who asks for what (the signed-in email and
 * the subject, read-only), whether there is anything to cancel or refund, and
 * the button that sends it.
 */
export default function RequestForm({
  buttonTitle,
  email,
  hasNoSubscription,
  heading,
  onSubmit,
  subject,
  submitting,
}: {
  buttonTitle: string
  email: string | undefined
  hasNoSubscription: boolean
  heading: string
  onSubmit: () => void
  subject: string
  submitting: boolean
}) {
  const { t } = useTranslation()

  return (
    <HelpCenterLayout>
      <h1 className="vm-heading animate-fade-up">{heading}</h1>

      <dl className="vm-card animate-fade-up overflow-hidden [animation-delay:80ms]">
        <div className="vm-row flex-col items-start gap-0.5">
          <dt className="text-xs font-medium uppercase tracking-wide text-vm-muted">
            {t("pages.helpCenter.shared.emailAddress").replace(
              /\s*[:：]\s*$/,
              "",
            )}
          </dt>
          <dd
            className="text-[15px] font-semibold text-vm-ink [overflow-wrap:anywhere]"
            data-testid="help-center-email"
          >
            {email}
          </dd>
        </div>

        <div className="vm-row flex-col items-start gap-0.5">
          <dt className="text-xs font-medium uppercase tracking-wide text-vm-muted">
            {t("pages.helpCenter.shared.subject")}
          </dt>
          <dd className="text-[15px] font-semibold text-vm-ink">{subject}</dd>
        </div>
      </dl>

      {hasNoSubscription && (
        <p
          className="vm-note flex animate-fade-in items-center justify-center gap-2 bg-vm-yellow-soft text-vm-ink"
          role="status"
        >
          <ExclamationTriangleIcon aria-hidden="true" className="h-5 w-5" />
          {t("pages.helpCenter.shared.noActiveSubscription")}
        </p>
      )}

      <div className="mt-auto animate-fade-up pt-4 [animation-delay:160ms] sm:mt-2">
        <Button
          disabled={hasNoSubscription}
          onClick={onSubmit}
          submitting={submitting}
          title={buttonTitle}
          variant="primary"
        />
      </div>
    </HelpCenterLayout>
  )
}
